import React from 'react';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { ShadowField, type ShadowValue } from './ShadowField';
import type { DropShadowSettings } from '@/components/inspector-fields/DropShadowMenu/DropShadowMenu';

const DEFAULT_SHADOW_SETTINGS: DropShadowSettings = {
  x: 0,
  y: 0,
  blur: 0,
  spread: 0,
  color: '#000000',
};

interface NodeShadowFieldProps {
  /** Prop name for the shadow type ('none' | 'drop'). Defaults to 'shadowType'. */
  shadowTypeProp?: string;
  /** Prop name for the shadow settings object. Defaults to 'shadowSettings'. */
  shadowSettingsProp?: string;
  className?: string;
}

const NodeShadowField: React.FC<NodeShadowFieldProps> = ({
  shadowTypeProp = 'shadowType',
  shadowSettingsProp = 'shadowSettings',
  className = 'max-w-[216px]',
}) => {
  const { id, props: nodeProps, node, actions } = useInspectorNode();
  const { setBPProp, getBPProp } = useBreakpointAwareProp(node?.data.displayName || '');

  // shadowTypeProp/shadowSettingsProp are dynamic strings, so T[K] tries to resolve to unknown
  const shadowType = (getBPProp(nodeProps, shadowTypeProp) as ShadowValue) || 'none';

  const shadowSettings =
    (getBPProp(nodeProps, shadowSettingsProp) as DropShadowSettings | undefined) ||
    DEFAULT_SHADOW_SETTINGS;

  const handleChange = (value: ShadowValue) => {
    actions.setProp(id, (props: Record<string, unknown>) => {
      setBPProp(props, shadowTypeProp, value);
      if (value === 'drop' && !getBPProp(nodeProps, shadowSettingsProp)) {
        setBPProp(props, shadowSettingsProp, { ...DEFAULT_SHADOW_SETTINGS });
      }
    });
  };

  const handleSettingsChange = (next: DropShadowSettings) => {
    actions.setProp(id, (props: Record<string, unknown>) => {
      setBPProp(props, shadowSettingsProp, next);
    });
  };

  return (
    <ShadowField
      className={className}
      value={shadowType}
      settings={shadowSettings}
      onChange={handleChange}
      onSettingsChange={handleSettingsChange}
    />
  );
};

export default NodeShadowField;
